import { isEmptyWhereClause } from '@modules/common/utils';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { useGetQuantifyAggregatePeriod } from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyDashboardFilters } from '@modules/quantify/store/types';
import {
  getCombinedQuantifyAndDashboardFilters,
  getQuantifyAggregatePeriodFromDuration,
} from '@modules/quantify/utils';
import { TimeUnit } from 'generated/graphql';
import { useRef } from 'react';
import { useUpdateEffect } from 'react-use';

interface UseUpdateQuantifyStateForGlobalFiltersOptiosn {
  skip: boolean;
}

export const useUpdateQuantifyStateForGlobalFilters = ({
  skip,
}: UseUpdateQuantifyStateForGlobalFiltersOptiosn) => {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const query = useQuantifyStore(({ query }) => query);

  const globalFilterDuration = useDashboardStore(({ query }) => query.duration);
  const globalFilterWhereClause = useDashboardStore(
    ({ query }) => query.whereClause,
  );

  const quantifyAggregatePeriod = useGetQuantifyAggregatePeriod();

  const dependencies = { query };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useUpdateEffect(() => {
    if (skip) {
      return;
    }

    const isGlobalFiltersApplied =
      !!globalFilterDuration || !isEmptyWhereClause(globalFilterWhereClause);

    const updatedDashboardFilters: QuantifyDashboardFilters | null =
      isGlobalFiltersApplied
        ? {
            duration: globalFilterDuration,
            whereClause: !isEmptyWhereClause(globalFilterWhereClause)
              ? globalFilterWhereClause
              : undefined,
            aggregatePeriod: globalFilterDuration
              ? getQuantifyAggregatePeriodFromDuration({
                  duration: globalFilterDuration,
                  currentAggregatePeriod:
                    quantifyAggregatePeriod ?? TimeUnit.Month,
                })
              : undefined,
          }
        : null;

    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: {
        dashboardFilters: updatedDashboardFilters,
        quantifyAndDashboardFilters: getCombinedQuantifyAndDashboardFilters({
          primaryQuery: dependenciesRef.current.query,
          dashboardFilters: updatedDashboardFilters,
        }),
      },
    });
  }, [globalFilterDuration, globalFilterWhereClause, skip]);
};
